import type { FinalResult, KeyFactor } from '../types'
import { marked } from '../utils/marked'
import { linkifyPlainUrls, stripUnhelpfulCitations } from '../utils/text'
import type React from 'react'

function normaliseImpact(s: string): string {
  return (s || '').trim()
}

function directionLabel(dir: KeyFactor['direction']): string {
  return dir === 'up' ? '↑ Increases' : '↓ Decreases'
}

function directionClass(): string {
  // Keep layout classes here; colour is applied via inline styles to guarantee palette correctness.
  return 'shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wider whitespace-nowrap'
}

function displayShortName(s: string): string {
  return (s || '').replace(/_/g, ' ').trim()
}

function directionStyle(dir: KeyFactor['direction']): React.CSSProperties {
  const up = dir === 'up'
  const colour = up ? 'var(--green)' : 'var(--clay-red)'
  return {
    color: colour,
    borderColor: up ? 'rgba(47, 107, 61, 0.35)' : 'rgba(168, 72, 72, 0.35)',
    backgroundColor: up ? 'rgba(47, 107, 61, 0.10)' : 'rgba(168, 72, 72, 0.10)',
  }
}

export default function SupervisorSummaryCard({
  finalResult,
  embedded = false,
}: {
  finalResult?: FinalResult
  embedded?: boolean
}) {
  if (!finalResult) return null

  const exec = linkifyPlainUrls(stripUnhelpfulCitations((finalResult.executive_summary || finalResult.summary || '').trim()))
  const factors = Array.isArray(finalResult.key_factors) ? finalResult.key_factors : []

  const content = (
    <>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="font-semibold text-lg text-ink">Executive summary</div>
      </div>

      {exec ? (
        <div
          className="mt-3 text-sm text-ink/90 leading-relaxed [&>p]:mt-3 [&>p:first-child]:mt-0 [&>ul]:mt-3 [&>ol]:mt-3"
          dangerouslySetInnerHTML={{ __html: marked.parse(exec, { breaks: true }) }}
        />
      ) : (
        <div className="mt-3 text-sm text-muted">Executive summary will appear here once the supervisor completes.</div>
      )}

      {factors.length > 0 && (
        <div className="mt-4">
          <div className="font-semibold text-ink">Watchlist events</div>
          <div className="mt-1 text-sm text-muted">
            Future developments to watch — each item notes what could move the probability up or down.
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {factors.slice(0, 5).map((f, idx) => (
              <div key={idx} className="rounded-delphi border border-rule bg-paper p-3 overflow-hidden">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 font-semibold text-ink leading-snug break-words pr-2">
                    {displayShortName(f.short_name)}
                  </div>
                  <span
                    className={directionClass()}
                    style={directionStyle(f.direction)}
                    title={f.direction === 'up' ? 'Would increase probability' : 'Would decrease probability'}
                  >
                    {directionLabel(f.direction)}
                  </span>
                </div>
                <div className="mt-2 text-sm text-ink/90 leading-relaxed">{f.description}</div>
                {normaliseImpact(f.percentage_point_impact) && (
                  <div className="mt-2 text-[11px] text-muted">
                    Impact: <span className="text-ink">{normaliseImpact(f.percentage_point_impact)} pp</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  )

  if (embedded) {
    return <div>{content}</div>
  }

  return (
    <section className="rounded-delphi border border-rule bg-paper2 shadow-delphi p-4 sm:p-5">
      {content}
    </section>
  )
}


